import { useTranslation } from 'react-i18next';

import {
    Body1,
    Body2,
    FilledCard,
    Stack,
    Subtitle1,
    Subtitle2,
} from '@myphx-engineering/phx-design-system';

import type {
    AuthorizedMedicationInfoFragment,
    UnknownMedicationInfoFragment,
} from '../../../graphql/generated/graphql';
import { useMedicationPricing } from '../../../hooks/use-medication-pricing';
import { formatToLocalizedCurrency } from '../../../util';
import type { PricingInfo } from '../../../util/config/get-pricing';
import { ConditionalComponent } from '../../utility/ConditionalComponent';

type PreviewTempOrfoDetailsProps = {
    pricingInfo: PricingInfo;
    medication?:
        | AuthorizedMedicationInfoFragment
        | UnknownMedicationInfoFragment;
};

export const PreviewTempOrfoDetails = ({
    pricingInfo,
    medication,
}: PreviewTempOrfoDetailsProps) => {
    const { t } = useTranslation();

    const { price, savingsAmount } = useMedicationPricing(
        pricingInfo,
        medication
    );

    const pricing = {
        option: t('pricing.cashPrice'),
        value:
            savingsAmount === 0
                ? `${price}*`
                : t('pricing.cashValue', { price }),
        details: savingsAmount ? (
            <Body2>
                {t('pricing.orfoSavingsProgram', {
                    amount: formatToLocalizedCurrency(savingsAmount),
                })}
            </Body2>
        ) : undefined,
    };

    return (
        <>
            <FilledCard withBorder data-testid="pharmacy-preview-pricing-card">
                <Stack gap="xxxs">
                    <Subtitle2>{pricing.option}</Subtitle2>
                    <Subtitle1>{pricing.value}</Subtitle1>
                    {pricing.details}
                </Stack>
            </FilledCard>

            <Stack gap="xs" data-testid="pharmacy-preview-terms-and-conditions">
                <ConditionalComponent condition={Boolean(savingsAmount)}>
                    <Body1>
                        {t('previewDetails.termsAndConditions.orfo.body1')}
                    </Body1>
                    <Body1>
                        {t('previewDetails.termsAndConditions.orfo.body2')}
                    </Body1>
                    <Body1>
                        {t('previewDetails.termsAndConditions.orfo.body3')}
                    </Body1>
                </ConditionalComponent>
                <Body2>{t('previewDetails.orfoDescription')}</Body2>
            </Stack>
        </>
    );
};
